import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ordenCompraService, { OrdenCompra } from '../../services/ordenCompraService';
import ubicacionService from '../../services/ubicacionService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = nroComprobante ?? 0;
  return `${L}-${suc}-${String(nro).padStart(8, '0')}`;
};

const OrdenCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [orden, setOrden] = useState<OrdenCompra | null>(null);
  const [ubicacionDestinoNombre, setUbicacionDestinoNombre] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const loadOrden = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id)) {
      enqueueSnackbar('Orden de compra no válida', { variant: 'error' });
      navigate('/compras/ordenes');
      return;
    }

    try {
      setLoading(true);
      const data = await ordenCompraService.getOrdenCompra(id);
      setOrden(data);

      if (data?.nroUbicacionDestino) {
        try {
          const u = await ubicacionService.getUbicacion(data.nroUbicacionDestino);
          setUbicacionDestinoNombre(u?.nombreUbicacion?.trim() || '');
        } catch {
          setUbicacionDestinoNombre('');
        }
      } else {
        setUbicacionDestinoNombre('');
      }
    } catch (error) {
      console.error('Error al cargar orden de compra:', error);
      enqueueSnackbar('Error al cargar la orden de compra', { variant: 'error' });
      navigate('/compras/ordenes');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadOrden();
  }, [loadOrden]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!orden) return null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/compras/ordenes'))}
              sx={{ color: '#1976d2', alignSelf: 'flex-start' }}
            >
              Volver
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              Orden de Compra {formatoLetraSucNro(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro. Formulario
              </Typography>
              <Typography variant="body1">{orden.nroFormulario}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(orden.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Comprobante
              </Typography>
              <Typography variant="body1">
                {formatoLetraSucNro(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}
              </Typography>
            </Grid>
            {orden.ordenDeCompra ? (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Orden de compra (ref.)
                </Typography>
                <Typography variant="body1">{orden.ordenDeCompra}</Typography>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body1">
                {orden.nroProveedor != null ? orden.nroProveedor : '-'} - {orden.nombre || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                CUIT
              </Typography>
              <Typography variant="body1">{orden.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Ubicación destino
              </Typography>
              <Typography variant="body1">
                {orden.nroUbicacionDestino != null
                  ? ubicacionDestinoNombre
                    ? `${orden.nroUbicacionDestino} - ${ubicacionDestinoNombre}`
                    : String(orden.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1">{orden.usuario || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Importe total
              </Typography>
              <Typography variant="body1">{formatCurrency(orden.importeTotal ?? 0)}</Typography>
            </Grid>
            {orden.descripcion ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">{orden.descripcion}</Typography>
              </Grid>
            ) : null}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    P. unitario
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Subtotal
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(orden.detalles || []).map((d, idx) => (
                  <TableRow key={`${d.nroIdformulario}-${d.nroItem}-${idx}`}>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">
                      {Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                    <TableCell>{d.notas || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdenCompraDetalle;
